import React, { useMemo, useState } from "react";
import * as moment from "moment-timezone";
import { toCamelCase } from "../../../../hooks/toCameCase.tsx";
import RowLoading from "./RowLoading.tsx";
import EmptyImage from "../../../EmptyImage.tsx";
import { fixedHeadStyle, fixedStyle } from "../../../../utils/constanst.js";

export interface RestDayPartnerRow {
  partner_id: number;
  partner_firstName: string;
  partner_lastName: string;
  partner_secondLastName: string;
  position_name: string;
  branch_id: number;
  branch_name: string;
  shift: string;
  rest_day: string;
}

export interface RestDayDayRecord {
  date: string;
  partners: RestDayPartnerRow[];
}

const TableDiasDescanso = ({
  records,
  loading,
}: {
  records: RestDayDayRecord[];
  loading: boolean;
}) => {
  const [sortBy, setSortBy] = useState("partner");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const flatRows = useMemo(() => {
    const rows: (RestDayPartnerRow & { date: string })[] = [];
    records.forEach((dayRecord) => {
      dayRecord.partners.forEach((partner) => {
        rows.push({ ...partner, date: dayRecord.date });
      });
    });
    return rows;
  }, [records]);

  const sortedRows = useMemo(() => {
    const sorted = [...flatRows];
    const dir = sortOrder === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
      switch (sortBy) {
        case "date":
          return (
            moment.utc(a.date).valueOf() - moment.utc(b.date).valueOf()
          ) * dir;
        case "position":
          return a.position_name.localeCompare(b.position_name, "es") * dir;
        case "shift":
          return (a.shift || "").localeCompare(b.shift || "", "es") * dir;
        case "branch":
          return a.branch_name.localeCompare(b.branch_name, "es") * dir;
        case "rest_day":
          return a.rest_day.localeCompare(b.rest_day, "es") * dir;
        case "partner":
        default: {
          const nameA = `${a.partner_lastName} ${a.partner_firstName}`;
          const nameB = `${b.partner_lastName} ${b.partner_firstName}`;
          return nameA.localeCompare(nameB, "es") * dir;
        }
      }
    });

    return sorted;
  }, [flatRows, sortBy, sortOrder]);

  const setSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const getIconSort = (column: string) => {
    if (sortBy !== column) return null;
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"}`}
      />
    );
  };

  const getShiftName = (shift: string): string => {
    const shiftMap: { [key: string]: string } = {
      Matutino: "Matutino",
      Intermedio: "Intermedio",
      Vespertino: "Vespertino",
    };
    return shiftMap[shift] || shift || "-";
  };

  return (
    <div className="table-responsive mb-3">
      <table className="table table-hover table-panel text-nowrap align-middle mb-0">
        <thead>
          <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
            <th onClick={() => setSort("date")}>
              Fecha {getIconSort("date")}
            </th>
            <th onClick={() => setSort("partner")}>
              Colaborador {getIconSort("partner")}
            </th>
            <th onClick={() => setSort("position")}>
              Posición {getIconSort("position")}
            </th>
            <th onClick={() => setSort("shift")}>
              Turno {getIconSort("shift")}
            </th>
            <th onClick={() => setSort("rest_day")}>
              Día de descanso {getIconSort("rest_day")}
            </th>
            <th onClick={() => setSort("branch")}>
              Sucursal {getIconSort("branch")}
            </th>
          </tr>
        </thead>
        <tbody>
          {!loading &&
            sortedRows.map((row, index) => (
              <tr key={`${row.date}-${row.partner_id}-${index}`}>
                <td>{moment.utc(row.date).format("DD-MM-YYYY")}</td>
                <td style={{ ...fixedStyle } as React.CSSProperties}>
                  {toCamelCase(
                    `${row.partner_firstName} ${row.partner_lastName} ${row.partner_secondLastName}`
                  )}
                </td>
                <td>{row.position_name}</td>
                <td>{getShiftName(row.shift)}</td>
                <td>{row.rest_day}</td>
                <td>{row.branch_name}</td>
              </tr>
            ))}

          {loading &&
            Array.from({ length: 15 }).map((_, index) => (
              <RowLoading key={index} />
            ))}

          {!loading && sortedRows.length === 0 && (
            <tr>
              <td colSpan={6}>
                <div className="py-4 d-flex flex-column align-items-center justify-content-center">
                  <EmptyImage className="mx-auto w-250px" />
                  <h5 className="text-secondary text-center fs-20px mt-3">
                    No se encontraron colaboradores con día de descanso
                  </h5>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableDiasDescanso;
